import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { computeIsGermanDefault, getCurrentLangCode } from '../utils/language';
import generateInvoicePDF from '../utils/generateInvoicePDF';

function getRestBase() {
  if (typeof window !== 'undefined' && window.rypLoginConfig && window.rypLoginConfig.restUrl) {
    return window.rypLoginConfig.restUrl;
  }
  return (window.location.origin || '') + '/?rest_route=/ryp/v1/';
}

function buildRestUrl(endpoint) {
  const base = getRestBase();
  if (base.includes('rest_route=')) {
    return base.replace(/\/$/, '').replace(/\/ryp\/v1\/?$/, '') + '/ryp/v1/' + endpoint;
  }
  return base + endpoint;
}

const PRODUCT_NAMES = {
  standard: { en: 'Standard Fit', de: 'Standart Produkt', pl: 'Produkt standardowy' },
  premium: { en: 'Premium Fit', de: 'Premium Produkt', pl: 'Produkt premium' },
};

const STATUS_CONFIG = {
  pending:           { en: 'Pending',          de: 'Ausstehend',         pl: 'Oczekujące',              color: 'default' },
  awaiting_payment:  { en: 'Awaiting Payment', de: 'Zahlung ausstehend', pl: 'Oczekuje na płatność',     color: 'warning' },
  pending_confirmation: { en: 'Pending Review', de: 'Pruefung ausstehend', pl: 'Oczekuje na weryfikację', color: 'warning' },
  paid:              { en: 'Paid',             de: 'Bezahlt',            pl: 'Opłacone',                color: 'success' },
  suspect:          { en: 'Suspect',         de: 'Verdächtig',         pl: 'Podejrzane',             color: 'warning' },
  expired:           { en: 'Expired',          de: 'Abgelaufen',         pl: 'Wygasłe',                 color: 'default' },
  failed:            { en: 'Failed',           de: 'Fehlgeschlagen',     pl: 'Nieudane',                color: 'error' },
  cancelled:         { en: 'Cancelled',        de: 'Storniert',          pl: 'Anulowane',               color: 'default' },
};

const BILLING_HISTORY_LIMIT = 1;

function formatSwissDateTime(value, locale) {
  if (!value) return '';
  // MySQL DATETIME (e.g. "2026-02-24 13:08:00") has no timezone.
  // Our DB stores it in UTC, so we must parse it as UTC, then display in Europe/Zurich.
  const raw = String(value).trim();
  const normalized = raw.replace(' ', 'T');
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
  const iso = hasTimezone ? normalized : (normalized + 'Z'); // Force UTC if timezone missing
  const asDate = new Date(iso);
  if (Number.isNaN(asDate.getTime())) return String(value);
  return asDate.toLocaleString(locale, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Zurich',
  });
}

export default function BillingPage({ setCurrentStep }) {
  const [isGerman, setIsGerman] = useState(() => computeIsGermanDefault());
  const [billingItems, setBillingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadNotice, setDownloadNotice] = useState({ open: false, severity: 'info', text: '' });
  const isPolish = getCurrentLangCode() === 'pl';

  useEffect(() => {
    setIsGerman(computeIsGermanDefault());
  }, []);

  useEffect(() => {
    const locale = isPolish ? 'pl-PL' : (isGerman ? 'de-CH' : 'en-GB');

    async function fetchOrders() {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const url = buildRestUrl('orders');
          const sep = url.includes('?') ? '&' : '?';
          const response = await fetch(url + sep + 'token=' + encodeURIComponent(token));
          const data = await response.json();
          if (data.success && Array.isArray(data.orders) && data.orders.length > 0) {
            const latestOrders = data.orders.slice(0, BILLING_HISTORY_LIMIT);
            const items = latestOrders.map((order) => ({
              id: order.id,
              date: formatSwissDateTime(order.created_at, locale),
              rawDate: order.created_at,
              product: getProductName(order.product, isGerman, isPolish),
              productKey: order.product,
              price: parseFloat(order.amount),
              currency: order.currency || 'CHF',
              status: order.status,
              paymentMethod: order.payment_method,
            }));
            setBillingItems(items);
            setLoading(false);
            return;
          }
        }
      } catch (e) {
        console.error('Failed to fetch orders:', e);
      }

      // Fallback: load from localStorage
      const paymentDataStr = localStorage.getItem('paymentData');
      if (paymentDataStr) {
        const paymentData = JSON.parse(paymentDataStr);
        setBillingItems([{
          id: paymentData.orderId || 1,
          date: formatSwissDateTime(paymentData.timestamp, locale),
          rawDate: paymentData.timestamp,
          product: getProductName(paymentData.product, isGerman, isPolish),
          productKey: paymentData.product || 'standard',
          price: parseFloat(paymentData.productPrice || '100.00'),
          currency: String(paymentData.currency || localStorage.getItem('selectedCurrency') || 'CHF').toUpperCase(),
          status: paymentData.status || 'pending',
          paymentMethod: paymentData.paymentMethod || 'wise',
        }]);
      } else {
        setBillingItems([{
          id: 1,
          date: formatSwissDateTime(new Date().toISOString(), locale),
          rawDate: new Date().toISOString(),
          product: isPolish ? 'Produkt standardowy' : (isGerman ? 'Standart Produkt' : 'Standard Fit'),
          productKey: 'standard',
          price: 100.00,
          currency: 'CHF',
          status: 'pending',
          paymentMethod: 'wise',
        }]);
      }
      setLoading(false);
    }

    fetchOrders();
  }, [isGerman, isPolish]);

  const getProductName = (productId, german, polish) => {
    const p = PRODUCT_NAMES[productId];
    if (p) return polish ? p.pl : (german ? p.de : p.en);
    return PRODUCT_NAMES['standard'][polish ? 'pl' : (german ? 'de' : 'en')];
  };

  const getStatusChip = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Chip
        label={isPolish ? cfg.pl : (isGerman ? cfg.de : cfg.en)}
        color={cfg.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getText = (key) => {
    if (isPolish) {
      const polishTexts = {
        title: 'Faktura',
        subtitle: 'Twoje zamówienia',
        back: 'Wstecz',
        nr: 'Nr',
        date: 'Data/Czas',
        product: 'Wybrany produkt',
        price: 'Cena',
        status: 'Status',
        download: 'PDF',
        seeResults: 'Zobacz wyniki',
        noItems: 'Nie znaleziono faktur'
      };
      return polishTexts[key] || key;
    }
    if (isGerman) {
      const germanTexts = {
        title: 'Rechnung',
        subtitle: 'Ihre Bestellungen',
        back: 'Zurück',
        nr: 'Nr.',
        date: 'Datum/Zeit',
        product: 'Gewähltes Produkt',
        price: 'Preis',
        status: 'Status',
        download: 'PDF',
        seeResults: 'Ergebnisse ansehen',
        noItems: 'Keine Rechnungen gefunden'
      };
      return germanTexts[key] || key;
    }
    const englishTexts = {
      title: 'Billing',
      subtitle: 'Your orders',
      back: 'Back',
      nr: 'No.',
      date: 'Date/Time',
      product: 'Chosen Product',
      price: 'Price',
      status: 'Status',
      download: 'PDF',
      seeResults: 'See Results',
      noItems: 'No bills found'
    };
    return englishTexts[key] || key;
  };

  const generatePDF = async (item) => {
    const token = localStorage.getItem('auth_token');
    // Keep the previous invoice download behavior: generate in the frontend template
    // so the customer-facing layout remains unchanged.
    let clientDetails = null;
    try {
      if (token && item.id) {
        const url = buildRestUrl('orders/' + item.id);
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(url + sep + 'token=' + encodeURIComponent(token));
        const data = await res.json();
        if (data.success && data.order) {
          clientDetails = data.order.client_details;
        }
      }
    } catch (e) {
      // Fallback to localStorage
    }
    if (!clientDetails) {
      const stored = localStorage.getItem('paymentData');
      if (stored) {
        const pd = JSON.parse(stored);
        clientDetails = {
          companyName: pd.companyName, firstName: pd.firstName, lastName: pd.lastName,
          street: pd.street, houseNumber: pd.houseNumber, zipCode: pd.zipCode,
          town: pd.town, country: pd.country, email: pd.email,
        };
      }
    }
    const doc = await generateInvoicePDF({
      id: item.id,
      product: item.productKey || 'standard',
      amount: item.price,
      currency: item.currency,
      date: item.rawDate || new Date().toISOString(),
      clientDetails: clientDetails || {},
    });
    doc.save(`invoice_${item.id}_${new Date().toISOString().split('T')[0]}.pdf`);
    setDownloadNotice({
      open: true,
      severity: 'success',
      text: isPolish
        ? 'Pobrano fakturę.'
        : (isGerman ? 'Rechnung heruntergeladen.' : 'Invoice downloaded.'),
    });
  };

  const handleSeeResults = () => {
    setCurrentStep({ activeStep: 5, status: 'billing-viewed' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          onClick={() => setCurrentStep({ activeStep: 3, status: 'back-from-billing' })}
          sx={{ textTransform: 'none' }}
        >
          {getText('back')}
        </Button>
      </Box>

      <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 2 }}>
        {getText('title')}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {getText('subtitle')}
      </Typography>

      <Card>
        <CardContent>
          {billingItems.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {getText('noItems')}
            </Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>{getText('nr')}</strong></TableCell>
                      <TableCell><strong>{getText('date')}</strong></TableCell>
                      <TableCell><strong>{getText('product')}</strong></TableCell>
                      <TableCell align="right"><strong>{getText('price')}</strong></TableCell>
                      <TableCell align="center"><strong>{getText('status')}</strong></TableCell>
                      <TableCell align="center"><strong>{getText('download')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billingItems.map((item, index) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>{item.product}</TableCell>
                        <TableCell align="right">
                          <strong>{item.price.toFixed(2)} {item.currency}</strong>
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(item.status)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            onClick={() => generatePDF(item)}
                            aria-label={getText('download')}
                            sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSeeResults}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ minWidth: 200, textTransform: 'none' }}
                >
                  {getText('seeResults')}
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={downloadNotice.open}
        autoHideDuration={4500}
        onClose={() => setDownloadNotice((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setDownloadNotice((prev) => ({ ...prev, open: false }))}
          severity={downloadNotice.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {downloadNotice.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
